import type { PlaybackIntent } from "@/player/lib/scheduling/types";

export type AudioSurfaceAction = "play" | "unchanged" | "pause_bed";
export type VisualSurfaceAction =
  | "unchanged"
  | "play_muted"
  | "play_unmuted"
  | "replace_signage"
  | "replace_foreground";

export interface IntentSurfacePlan {
  audio: AudioSurfaceAction;
  visual: VisualSurfaceAction;
}

/**
 * Map a playback intent (the "bus") to how each surface should react when a
 * timed event or boundary item fires. Muting is decided by the intent alone:
 * `video_muted` is the explicit "show the video, silence its own audio" intent.
 */
export function planIntentSurfaces(intent: PlaybackIntent): IntentSurfacePlan {
  switch (intent) {
    case "audio_only":
      return { audio: "play", visual: "unchanged" };
    case "video_only":
      return { audio: "unchanged", visual: "play_muted" };
    case "video_muted":
      return { audio: "unchanged", visual: "play_muted" };
    case "av_full":
      return {
        audio: "play",
        visual: "replace_foreground",
      };
    default:
      return { audio: "unchanged", visual: "unchanged" };
  }
}

export function isForegroundIntent(intent: PlaybackIntent): boolean {
  return intent === "av_full";
}

/**
 * Whether the visual surface's own audio should be muted. `video_only` and
 * `video_muted` always mute; `av_full` plays the video's audio.
 */
export function visualShouldMute(intent: PlaybackIntent): boolean {
  if (intent === "video_only" || intent === "video_muted") return true;
  if (intent === "av_full") return false;
  return true;
}
